import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../index";

interface AuthState {
  user: {
    id: number;
    email: string;
    role: string;
    labelId?: number;
    labelName?: string;
    labelType?: string;
    exp?: number;
    iat?: number;
    impersonated?: boolean;
  } | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const loadAuthState = (): AuthState | null => {
  try {
    const serializedState = localStorage.getItem("auth");
    if (serializedState) {
      const state = JSON.parse(serializedState);
      if (state.user?.exp) {
        if (Math.floor(Date.now() / 1000) >= state.user.exp) {
          localStorage.removeItem("auth");
          return null;
        }
      }
      return state;
    }
    return null;
  } catch (error) {
    console.error("Error loading authentication state:", error);
    return null;
  }
};

const saveAuthState = (state: AuthState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("auth", serializedState);
  } catch (error) {
    console.error("Error saving authentication state:", error);
  }
};

const initialState: AuthState = loadAuthState() || {
  token: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<Partial<AuthState>>) => {
      const newState = {
        ...state,
        ...action.payload,
        isAuthenticated: true,
      };
      saveAuthState(newState);
      return newState;
    },
    clearAuth: (state) => {
      localStorage.removeItem("auth");
      localStorage.removeItem("draft_pitches");

      localStorage.removeItem("cadence_tour_minimized");
      localStorage.removeItem("cadence_tour_step");
      localStorage.removeItem("cadence_tour_section");

      const draftReminderKeys = [
        "draft_reminder_dismissed_",
        "draft_reminder_snoozed_",
        "dueRemindersFetched",
      ];
      const keysToRemove: string[] = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && draftReminderKeys.some((prefix) => key.includes(prefix))) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      sessionStorage.removeItem("missingPhotosAlertSnoozed");

      return {
        token: null,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    },
    stopImpersonation: () => {
      const adminAuth = localStorage.getItem("adminAuth");
      localStorage.removeItem("adminAuth");

      if (adminAuth) {
        const parsed = JSON.parse(adminAuth);
        localStorage.setItem("auth", adminAuth);
        return {
          ...parsed,
          isAuthenticated: true,
        };
      }

      // Fallback: clear everything if no admin auth found
      localStorage.removeItem("auth");
      return {
        token: null,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { setAuth, clearAuth, stopImpersonation, setLoading, setError } = authSlice.actions;
export const selectAuth = (state: RootState) => state.auth;
export default authSlice.reducer;
